import { Injectable, inject } from '@angular/core';
import { QueryEntity } from '@datorama/akita';
import { Observable } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';

import { CmgUiUtilities } from '../../cmg-1003/components/common/cmg-ui-utils';
import {
  CombinedHousingExpenseModel,
  EmploymentIncomeExpenseModel,
  EmploymentModel,
  HousingExpenseDetailModel,
  IncomeModel,
} from '../../cmg-1003/models';
import { EmploymentStatus } from '../../cmg-1003/models/lookups/employment.lookups';
import { PrepaidItemType } from '../../cmg-1003/models/lookups/expenses.lookups';
import { nonNullish } from '../../shared/utils';
import { Cmg1003ApplicationInfoQuery } from '../application-info/application-info.query';
import { Cmg1003EmploymentIncomeExpensesStore } from './employment-income-expenses.store';
import { EmploymentIncomeExpensesUtils } from './employment-income-expenses.utils';
import { DirtyChecker2 } from '../core/dirty-checker-2';

@Injectable({ providedIn: 'root' })
export class Cmg1003EmploymentIncomeExpensesQuery extends QueryEntity<EmploymentIncomeExpenseModel> {
  protected store: Cmg1003EmploymentIncomeExpensesStore;
  private readonly _appQuery = inject(Cmg1003ApplicationInfoQuery);

  dirtyChecker2: DirtyChecker2 = new DirtyChecker2(this.store, this);
  constructor() {
    const store = inject(Cmg1003EmploymentIncomeExpensesStore);

    super(store);

    this.store = store;
  }

  assembleForSave(applicationId: number, forAutoRecovery = false) {
    const empIncExpEntity = this.getEntity(applicationId) as EmploymentIncomeExpenseModel;
    const processedIncomes = empIncExpEntity.grossMonthlyIncomes
      ?.map((t) => {
        if (t.incomeType === 0 && !forAutoRecovery) {
          const income = Object.assign({}, t);
          if (income.incomeId < 0) {
            income.incomeId = null;
          } else {
            income.isDeleted = true;
          }
          return income;
        } else {
          return t;
        }
      })
      .filter((t) => !!t.incomeId);

    return Object.assign({}, empIncExpEntity, {
      grossMonthlyIncomes: processedIncomes,
      combinedHousingExpenses: forAutoRecovery
        ? empIncExpEntity.combinedHousingExpenses
        : {
            ...empIncExpEntity.combinedHousingExpenses,
            otherHousingExpenses: this._assembleOtherHousingExpenses(empIncExpEntity.combinedHousingExpenses),
          },
    } as EmploymentIncomeExpenseModel);
  }

  /** Returns an Observable of the Employment, Income, and Expense data for the currently selected app. */
  selectActiveEmploymentIncomeExpense(): Observable<EmploymentIncomeExpenseModel> {
    return this._appQuery.selectActiveId().pipe(
      switchMap((appId) => {
        return this.selectEntity(appId, (employmentIncomeExpense: EmploymentIncomeExpenseModel) => employmentIncomeExpense);
      }),
    );
  }

  getEmploymentById(id: number, isPrimary: boolean, borrowerId?: number) {
    if (id === 0) {
      const emptyModel = new EmploymentModel();
      if (borrowerId) {
        emptyModel.borrowerId = borrowerId;
      }
      return emptyModel;
    }
    const empIncExpByAppId: EmploymentIncomeExpenseModel = this.getEntity(this._appQuery.getActiveId());
    return (isPrimary ? empIncExpByAppId.borrowerEmployers : empIncExpByAppId.coBorrowerEmployers).find((t) => t.employmentId === id);
  }

  /**
   * Borrower/CoBorrower is allowed only 1 primary employment. Once it is set, all other
   * employment has to be non-primary
   * @param isCoBorrower
   */
  selectPrimaryEmploymentId(isCoBorrower = false) {
    return this._appQuery.selectActiveId().pipe(
      switchMap((appId) => {
        return this.selectEntity(appId, (employmentIncomeExpense: EmploymentIncomeExpenseModel) => employmentIncomeExpense).pipe(
          filter((t) => !!t),
          map((t) =>
            isCoBorrower
              ? t.coBorrowerEmployers?.find(
                  (k) =>
                    k.employmentType === EmploymentStatus.Primary &&
                    k.isDeleted !== true &&
                    !EmploymentIncomeExpensesUtils.isDefaultByteEmployment(k),
                )
              : t.borrowerEmployers?.find(
                  (k) =>
                    k.employmentType === EmploymentStatus.Primary &&
                    k.isDeleted !== true &&
                    !EmploymentIncomeExpensesUtils.isDefaultByteEmployment(k),
                ),
          ),
          map((t) => (t ? t.employmentId : null)),
        );
      }),
    );
  }

  /**
   * Assembles the "otherHousingExpenses" array for saving.
   *
   * Behind the scenes, removes incomplete expenses, and merges expenses with same expenseType.
   */
  private _assembleOtherHousingExpenses(expenses: CombinedHousingExpenseModel) {
    // The original array of expenses from the API.
    const originalExpenses = (expenses as any).originalHousingExpenses ?? [];

    const visibleExpenses = expenses.otherHousingExpenses;

    // Subset of visibleExpenses where expenseType NOT in originalExpenses - e.g. expenseType: 100 (PrepaidItemType.OtherAmount).
    const originalExpenseTypes = originalExpenses.map((expInByte) => expInByte.expenseType);
    const differentExpenses: HousingExpenseDetailModel[] = visibleExpenses.filter(
      (visibleExp) => !originalExpenseTypes.includes(visibleExp.expenseType) && this._isExpenseEligibleForSave(visibleExp),
    );

    // The fully assembled and sanitized "otherHousingExpenses" array for save
    const assembledExpenses = [...originalExpenses, ...differentExpenses].map((exp) => {
      const matchingOriginalExpense = originalExpenses.find(
        (existingExp) => existingExp.expenseType === exp.expenseType && !!existingExp.proposedAmount,
      );

      const matchingVisibleExpense = visibleExpenses.filter((visibleExp) => visibleExp.expenseType === exp.expenseType)[0];

      return {
        ...exp,

        // The negative unique ID is a fallback, but will likely never occur.
        prepaidItemId: exp.expenseType === PrepaidItemType.OtherAmount ? null : exp.prepaidItemId || -CmgUiUtilities.getUniqueNumber(),

        proposedAmount: matchingVisibleExpense ? matchingVisibleExpense.proposedAmount : null,

        // True = The expense from the original 'otherHousingExpenses' array is no longer visible on the UI or is missing proposedAmount.
        isDeleted: !!matchingOriginalExpense && (!matchingVisibleExpense || !matchingVisibleExpense?.proposedAmount),
      };
    });

    return assembledExpenses;
  }

  private _isExpenseEligibleForSave(expense: HousingExpenseDetailModel) {
    return expense.expenseType !== PrepaidItemType.NotAssigned && nonNullish(expense.proposedAmount);
  }

  getEmploymentIncome$(employmentId: number, borrowerId?: number): Observable<IncomeModel[]> {
    return this._appQuery.selectActiveId().pipe(
      switchMap((appId) => {
        return this.selectEntity(appId, (employmentIncomeExpense: EmploymentIncomeExpenseModel) => employmentIncomeExpense.grossMonthlyIncomes).pipe(
          filter((t) => !!t),
          map((t) => t.filter((t) => t.borrowerId === borrowerId && t.employmentId === employmentId && t.isDeleted === false)),
        );
      }),
    );
  }

  /**
   * Other income of a borrower is the one where there is no employment Id.
   * @param borrowerId
   * @returns
   */
  getOtherIncome$(borrowerId?: number): Observable<IncomeModel[]> {
    return this._appQuery.selectActiveId().pipe(
      switchMap((appId) => {
        return this.selectEntity(appId, (employmentIncomeExpense: EmploymentIncomeExpenseModel) => employmentIncomeExpense.grossMonthlyIncomes).pipe(
          filter((t) => !!t),
          map((t) => t.filter((t) => t.borrowerId === borrowerId && !t.employmentId && t.isDeleted === false)),
          tap((t) => {
            if (t.length === 0) {
              t.push(IncomeModel.createEmpty(borrowerId)); // adds an empty row with a + button for adding additional items.
            }
          }),
        );
      }),
    );
  }

  selectActiveIncomeList$(borrowerId?: number): Observable<IncomeModel[]> {
    return this._appQuery.selectActiveId().pipe(
      switchMap((appId) => {
        return this.selectEntity(appId, (employmentIncomeExpense: EmploymentIncomeExpenseModel) => employmentIncomeExpense.grossMonthlyIncomes).pipe(
          filter((t) => !!t),
          map((t) =>
            t
              .filter((t) => t.borrowerId === borrowerId && !t.employmentId && t.isDeleted === false)
              .filter((t) => !t.isDeleted)
              .map((t) => Object.assign({ applicationId: appId }, t)),
          ),
        );
      }),
    );
  }

  getEmploymentByType$(employmentType: EmploymentStatus, isPrimaryBorrower: boolean): Observable<EmploymentModel[]> {
    return this._appQuery.selectActiveId().pipe(
      switchMap((appId) => {
        return this.selectEntity(appId, (employmentIncomeExpense: EmploymentIncomeExpenseModel) => employmentIncomeExpense).pipe(
          filter((t) => !!t),
          map((t) =>
            isPrimaryBorrower
              ? t.borrowerEmployers?.filter(
                  (k) => k.employmentType === employmentType && k.isDeleted !== true && !EmploymentIncomeExpensesUtils.isDefaultByteEmployment(k),
                )
              : t.coBorrowerEmployers?.filter(
                  (k) => k.employmentType === employmentType && k.isDeleted !== true && !EmploymentIncomeExpensesUtils.isDefaultByteEmployment(k),
                ),
          ),
        );
      }),
    );
  }

  getBorrowerEmploymentIncome$(borrowerId: number, isPrimaryBorrower: boolean): Observable<EmploymentModel[]> {
    return this._appQuery.selectActiveId().pipe(
      switchMap((appId) => {
        return this.selectEntity(appId, (employmentIncomeExpense: EmploymentIncomeExpenseModel) => employmentIncomeExpense);
      }),
      filter((t) => !!t),
      map((t) => {
        const employers = (isPrimaryBorrower ? t.borrowerEmployers : t.coBorrowerEmployers)?.filter(
          (k) => k.borrowerId === borrowerId && k.isDeleted !== true && !EmploymentIncomeExpensesUtils.isDefaultByteEmployment(k),
        );

        // Sort employers: Primary first, then SecondaryCurrent, then Former
        return (
          employers?.sort((a, b) => {
            if (a.employmentType === b.employmentType) {
              return 0;
            }
            return (b.employmentType ?? 0) - (a.employmentType ?? 0);
          }) || []
        );
      }),
    );
  }

  getTotalIncomeByBorrower$(borrowerId?: number): Observable<number> {
    return this._appQuery.selectActiveId().pipe(
      switchMap((appId) => {
        return this.selectEntity(appId, (employmentIncomeExpense: EmploymentIncomeExpenseModel) => employmentIncomeExpense).pipe(
          filter((t) => !!t),
          map((empIncExp) => {
            const validIncomes = EmploymentIncomeExpensesUtils.getValidCurrentEmploymentIncomes(empIncExp, borrowerId);
            return validIncomes.map((inc) => inc.amount).reduce((a, b) => a + b, 0) || 0;
          }),
        );
      }),
    );
  }

  getEmploymentMonthlyIncome$(employmentId: number, borrowerId?: number): Observable<number> {
    return this._appQuery.selectActiveId().pipe(
      switchMap((appId) => {
        return this.selectEntity(appId, (employmentIncomeExpense: EmploymentIncomeExpenseModel) => employmentIncomeExpense.grossMonthlyIncomes).pipe(
          filter((t) => !!t),
          map(
            (t) =>
              t
                .filter((t) => t.borrowerId === borrowerId && t.employmentId === employmentId && t.amount && t.isDeleted === false)
                .map((income) => income.amount)
                .reduce((a, b) => a + b, 0),
            0,
          ),
        );
      }),
    );
  }
}
